import { useEffect, useState } from "react";
import { api } from "../api";
import { organizeUiCatalog } from "../generated/organizeUiCatalog";

const originals = new WeakMap<Text, string>();
const appliedTranslations = new WeakMap<Text, string>();
const sessionTranslations = new Map<string, Record<string, string>>();
const loadedTranslationSets = new Set<string>();
const translationLoads = new Map<string, Promise<Record<string, string>>>();

type TranslationResponse = { translations: Record<string, string> };

async function storedTranslations(eventId: number, language: string) {
  const cacheKey = `${eventId}:${language.toLowerCase()}`;
  if (loadedTranslationSets.has(cacheKey)) return sessionTranslations.get(cacheKey) || {};
  const pending = translationLoads.get(cacheKey);
  if (pending) return pending;
  const load = api<TranslationResponse>(`/api/translations?eventId=${eventId}&language=${encodeURIComponent(language)}`)
    .then((result) => {
      const translations = { ...(sessionTranslations.get(cacheKey) || {}), ...result.translations };
      sessionTranslations.set(cacheKey, translations);
      loadedTranslationSets.add(cacheKey);
      return translations;
    })
    .finally(() => translationLoads.delete(cacheKey));
  translationLoads.set(cacheKey, load);
  return load;
}

function eligible(node: Text) {
  const parent = node.parentElement;
  const value = node.nodeValue || "";
  return Boolean(parent && value.trim() && !parent.closest("[data-no-translate],[contenteditable],script,style,textarea,code,pre") && !/^[-+0-9:/.·%]+$/.test(value.trim()));
}

export function usePageTranslation(eventId: number, language: string, canGenerate = true) {
  const [status, setStatus] = useState<{ loading: boolean; error: string }>({ loading: false, error: "" });
  useEffect(() => {
    const root = document.querySelector(".app-shell");
    if (!root || !language) return;
    let cancelled = false;
    let timer = 0;
    let running = false;

    async function translateVisibleText() {
      if (running || cancelled) return;
      running = true;
      setStatus({ loading: true, error: "" });
      try {
        const walker = document.createTreeWalker(root!, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        let current = walker.nextNode();
        while (current) {
          const node = current as Text;
          if (eligible(node)) {
            const currentValue = node.nodeValue || "";
            const lastAppliedValue = appliedTranslations.get(node);
            // React reutiliza el mismo nodo de texto cuando cambia un estado
            // (por ejemplo, de "Validando" a "Número vinculado"). Si el nodo
            // cambió desde el último valor aplicado por el traductor, ese nuevo
            // contenido pasa a ser el original. De lo contrario, el observador
            // restauraría para siempre el texto del estado anterior.
            if (!originals.has(node) || (lastAppliedValue !== undefined && currentValue !== lastAppliedValue)) {
              originals.set(node, currentValue);
            }
            nodes.push(node);
          }
          current = walker.nextNode();
        }
        // La traducción de la interfaz es un catálogo versionado, no una captura
        // de cada pantalla. Los datos dinámicos del evento no deben provocar
        // nuevas llamadas a IA cada vez que React vuelve a renderizarlos.
        const sources = /^es(?:-|$)/i.test(language) ? [] : [...organizeUiCatalog];
        const cacheKey = `${eventId}:${language.toLowerCase()}`;
        let translations: Record<string, string> = /^es(?:-|$)/i.test(language) ? {} : { ...(await storedTranslations(eventId, language)) };
        if (!/^es(?:-|$)/i.test(language) && sources.length) {
          const pendingSources = sources.filter((source) => !Object.prototype.hasOwnProperty.call(translations, source));
          if (pendingSources.length && canGenerate) {
            const result = await api<TranslationResponse>("/api/translations", { method: "POST", body: JSON.stringify({ eventId, language, texts: pendingSources, siteCatalog: true }) });
            Object.assign(translations, result.translations);
          }
          sessionTranslations.set(cacheKey, translations);
        }
        if (cancelled) return;
        if (!/^es(?:-|$)/i.test(language) && !canGenerate && !Object.keys(translations).length) {
          throw new Error("Todavía no hay una traducción de la interfaz disponible para este idioma. Habilitá la IA del evento para generarla.");
        }
        nodes.forEach((node) => {
          const original = originals.get(node) || "";
          const source = original.trim();
          const translated = translations[source] || source;
          const nextValue = original.replace(source, translated);
          appliedTranslations.set(node, nextValue);
          if (node.nodeValue !== nextValue) node.nodeValue = nextValue;
        });
        document.documentElement.lang = language;
        window.localStorage.setItem(`organize-language-${eventId}`, language);
        setStatus({ loading: false, error: "" });
      } catch (error) {
        if (!cancelled) setStatus({ loading: false, error: error instanceof Error ? error.message : "No se pudo traducir la página" });
      } finally { running = false; }
    }

    const schedule = () => { window.clearTimeout(timer); timer = window.setTimeout(() => { void translateVisibleText(); }, 120); };
    const observer = new MutationObserver((mutations) => {
      const needsTranslation = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        return !target?.closest("[data-no-translate]");
      });
      if (needsTranslation) schedule();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    void translateVisibleText();
    return () => { cancelled = true; window.clearTimeout(timer); observer.disconnect(); };
  }, [eventId, language, canGenerate]);
  return status;
}
